import PropTypes from 'prop-types';
import { useState } from 'react';

// Lib
import { INSTAGRAM } from '@/lib/socialProfile';
import { POST, STORY } from '@/lib/posts/postTypes';

// Hooks
import useMedia from '@/utils/hooks/swr/useMedia';

// Components
import Modal from '@/components/base/Modal';
import MediaViewerContent from '@/components/composite/MediaViewer/MediaViewerContent';
import MediaViewerNavigation from '@/components/composite/MediaViewer/MediaViewerNavigation';

const ModalContent = ({
    mediaId,
    mediaType = POST,
    group,
    socialProfile,
    mediaRequestParams = {},
    mutateMedias,
    withNextPrev,
    defaultTabsIndex
}) => {
    const [currentContentState, setCurrentContentState] = useState({
        mediaId: mediaId,
        socialProfile: socialProfile
    });

    const { media, previousMedia, nextMedia, isLoading, mutateMedia } = useMedia({
        groupID: group?.id,
        spID: socialProfile?.id,
        mediaID: currentContentState.mediaId,
        type: mediaType,
        requestParams: mediaRequestParams,
        withPrevNext: withNextPrev
    });

    const goToMedia = (media) => {
        if (!isLoading && media) {
            setCurrentContentState((prevState) => ({
                ...prevState,
                mediaId: media.id,
                ...(group
                    ? {
                          socialProfile: group.social_profiles.find(
                              (sp) => sp.id === media.social_profile_id
                          )
                      }
                    : {})
            }));
        }
    };

    const getNavMediaNetworkType = (media) => {
        return (
            (group
                ? group.social_profiles.find((sp) => sp.id === media?.social_profile_id)
                : currentContentState.socialProfile
            )?.social_network_type ?? INSTAGRAM
        );
    };

    return (
        <Modal.Body className="w-full">
            {withNextPrev && (
                <MediaViewerNavigation
                    post={previousMedia}
                    postType={mediaType}
                    isLoading={isLoading}
                    socialNetworkType={getNavMediaNetworkType(previousMedia)}
                    navAction={() => {
                        goToMedia(previousMedia);
                    }}
                    className="btn-mv-nav-prev"
                    navClick={() => {
                        goToMedia(previousMedia);
                    }}
                />
            )}
            <MediaViewerContent
                post={media}
                postType={mediaType}
                socialProfile={currentContentState.socialProfile}
                socialNetworkType={
                    currentContentState.socialProfile?.social_network_type ?? INSTAGRAM
                }
                socialNetworkId={currentContentState.socialProfile?.id}
                isLoading={isLoading}
                mutateMedia={mutateMedia}
                //Pass the mutate function to update the medias cache based on action inside the MediaViewer
                mutateMedias={mutateMedias}
                defaultTabsIndex={defaultTabsIndex}
            />
            {withNextPrev && (
                <MediaViewerNavigation
                    post={nextMedia}
                    postType={mediaType}
                    isLoading={isLoading}
                    socialNetworkType={getNavMediaNetworkType(nextMedia)}
                    navAction={() => {
                        goToMedia(nextMedia);
                    }}
                    isNext={true}
                    className="btn-mv-nav-next"
                    navClick={() => {
                        goToMedia(nextMedia);
                    }}
                />
            )}
        </Modal.Body>
    );
};

ModalContent.propTypes = {
    mediaId: PropTypes.string,
    mediaType: PropTypes.oneOf([POST, STORY]),
    group: PropTypes.object,
    socialProfile: PropTypes.object,
    mediaRequestParams: PropTypes.object,
    mutateMedias: PropTypes.func,
    withNextPrev: PropTypes.bool,
    defaultTabsIndex: PropTypes.number
};

const MediaViewerModal = ({
    isOpen,
    toggle,
    mediaId,
    group,
    socialProfile,
    mediaType = POST,
    mediaRequestParams = {},
    mutateMedias,
    withNextPrev = true,
    defaultTabsIndex = 0
}) => {
    return (
        <Modal size="full" open={isOpen} onDismiss={toggle}>
            <ModalContent
                mediaId={mediaId}
                mediaType={mediaType}
                group={group}
                socialProfile={socialProfile}
                mediaRequestParams={mediaRequestParams}
                //Pass the mutate function to update the medias cache based on action inside the MediaViewer
                mutateMedias={mutateMedias}
                withNextPrev={withNextPrev}
                defaultTabsIndex={defaultTabsIndex}
            />
        </Modal>
    );
};

MediaViewerModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    toggle: PropTypes.func.isRequired,
    mediaId: PropTypes.string,
    group: PropTypes.object,
    socialProfile: PropTypes.object,
    mediaType: PropTypes.oneOf([POST, STORY]),
    mediaRequestParams: PropTypes.object,
    mutateMedias: PropTypes.func,
    withNextPrev: PropTypes.bool,
    defaultTabsIndex: PropTypes.number
};

export default MediaViewerModal;
